import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { getPOSName } from "@faire/retailer-visitor-shared/features/PointOfSale/utils/getPOSName";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { RetailerOnboardingProgress } from "@faire/web-api--source/faire/retailers/pos/RetailerOnboardingProgress";
import { IRetailerLinkCohortEnum } from "@faire/web-api--source/indigofair/retailers/pos/IRetailerLinkCohortEnum";
import { IRetailerPosLinkSettings } from "@faire/web-api--source/indigofair/retailers/pos/IRetailerPosLinkSettings";
import { RetailerLinkState } from "@faire/web-api--source/indigofair/retailers/pos/RetailerLinkState";
import { useQuery as useLocationsQuery } from "@faire/web-api--source/retailers-pos-api/pos-locations/get-hooks";
import { useQuery as usePosSummaryQuery } from "@faire/web-api--source/retailers-pos-api/summary/get-hooks";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";
export var usePOSSummary = function() {
    var _summary_link_ref, _summary_settings, _summary_link_ref1;
    var isLoggedIn = useIsLoggedInRetailer();
    var _usePosSummaryQuery = usePosSummaryQuery(undefined, {
        staleTime: 1000 * 60 * 5,
        enabled: isLoggedIn
    }), summaryData = _usePosSummaryQuery.data, isLoading = _usePosSummaryQuery.isPending, isError = _usePosSummaryQuery.isError, refetch = _usePosSummaryQuery.refetch;
    var summary = useMemo(function() {
        return summaryData === null || summaryData === void 0 ? void 0 : summaryData.pos_link_summary;
    }, [
        summaryData
    ]);
    var settings = useMemo(function() {
        return summary === null || summary === void 0 ? void 0 : summary.settings;
    }, [
        summary
    ]);
    var type = summary === null || summary === void 0 ? void 0 : (_summary_link_ref = summary.link_ref) === null || _summary_link_ref === void 0 ? void 0 : _summary_link_ref.type;
    var name = type ? getPOSName(type) : "";
    var hasLinkedState = (summary === null || summary === void 0 ? void 0 : summary.retailer_link_state) === RetailerLinkState.RETAILER_LINK_LINKED;
    var hasLinkingState = (summary === null || summary === void 0 ? void 0 : summary.retailer_link_state) === RetailerLinkState.RETAILER_LINK_LINKING;
    var hasFinishedOnboarding = (summary === null || summary === void 0 ? void 0 : summary.onboarding_progress) === RetailerOnboardingProgress.V2_FINISHED || (summary === null || summary === void 0 ? void 0 : summary.onboarding_progress) === RetailerOnboardingProgress.FINISHED;
    var hasAutomaticInventory = (summary === null || summary === void 0 ? void 0 : (_summary_settings = summary.settings) === null || _summary_settings === void 0 ? void 0 : _summary_settings.inventory_update) === IRetailerPosLinkSettings.InventoryUpdate.AUTOMATIC;
    var disabledSettings = summary === null || summary === void 0 ? void 0 : summary.disabled_settings;
    var retailerLinkCohort = (summary === null || summary === void 0 ? void 0 : summary.retailer_link_cohort) ? IRetailerLinkCohortEnum.RetailerLinkCohort[summary === null || summary === void 0 ? void 0 : summary.retailer_link_cohort] : IRetailerLinkCohortEnum.RetailerLinkCohort.RETAILER_LINK_COHORT_UNKNOWN;
    var isLinkedCohort = [
        IRetailerLinkCohortEnum.RetailerLinkCohort.ACTIVE,
        IRetailerLinkCohortEnum.RetailerLinkCohort.INACTIVE
    ].includes(retailerLinkCohort);
    var _useLocationsQuery = useLocationsQuery({
        staleTime: 1000 * 60 * 5,
        enabled: hasLinkedState
    }), locationsData = _useLocationsQuery.data;
    var locations = useMemo(function() {
        var selectedLocation = settings === null || settings === void 0 ? void 0 : settings.selected_location;
        if (selectedLocation) {
            var _locationsData_locations;
            return uniqBy([
                selectedLocation
            ].concat(_to_consumable_array((_locationsData_locations = locationsData === null || locationsData === void 0 ? void 0 : locationsData.locations) !== null && _locationsData_locations !== void 0 ? _locationsData_locations : [])), function(location) {
                return location.id;
            });
        } else {
            var _locationsData_locations1;
            return (_locationsData_locations1 = locationsData === null || locationsData === void 0 ? void 0 : locationsData.locations) !== null && _locationsData_locations1 !== void 0 ? _locationsData_locations1 : [];
        }
    }, [
        locationsData,
        settings
    ]);
    var _summary_require_review_count, _summary_linked_count, _summary_archived_count, _summary_match_suggestion_count;
    return {
        isLoading: isLoading,
        isError: isError,
        refetch: refetch,
        type: type,
        name: name,
        businessName: summary === null || summary === void 0 ? void 0 : summary.business_name,
        shopifyId: summary === null || summary === void 0 ? void 0 : (_summary_link_ref1 = summary.link_ref) === null || _summary_link_ref1 === void 0 ? void 0 : _summary_link_ref1.shopify_merchant_id,
        reviewCount: (_summary_require_review_count = summary === null || summary === void 0 ? void 0 : summary.require_review_count) !== null && _summary_require_review_count !== void 0 ? _summary_require_review_count : 0,
        linkedCount: (_summary_linked_count = summary === null || summary === void 0 ? void 0 : summary.linked_count) !== null && _summary_linked_count !== void 0 ? _summary_linked_count : 0,
        archivedCount: (_summary_archived_count = summary === null || summary === void 0 ? void 0 : summary.archived_count) !== null && _summary_archived_count !== void 0 ? _summary_archived_count : 0,
        matchesCount: (_summary_match_suggestion_count = summary === null || summary === void 0 ? void 0 : summary.match_suggestion_count) !== null && _summary_match_suggestion_count !== void 0 ? _summary_match_suggestion_count : 0,
        settings: settings,
        locations: locations,
        isLinked: hasLinkedState || hasLinkingState,
        /**
         * This indicates whether the retailer has a functional POS connection or not,
         * which means a linked state AND a finished onboarding
         */ hasLinkedPOS: hasLinkedState && hasFinishedOnboarding,
        onboardingProgress: summary === null || summary === void 0 ? void 0 : summary.onboarding_progress,
        setupProgressPercent: summary === null || summary === void 0 ? void 0 : summary.setup_progress_percent,
        isInventoryStale: summary === null || summary === void 0 ? void 0 : summary.is_inventory_stale,
        defaultWeightUnit: summary === null || summary === void 0 ? void 0 : summary.default_weight_unit,
        createdAt: summary === null || summary === void 0 ? void 0 : summary.created_at,
        showReviewBanner: summary === null || summary === void 0 ? void 0 : summary.show_feedback_banner,
        hasAutomaticInventory: hasAutomaticInventory,
        disabledSettings: disabledSettings,
        retailerLinkCohort: retailerLinkCohort,
        isLinkedCohort: isLinkedCohort
    };
};
